'use client';

import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { clsx } from 'clsx';
import { CheckIcon } from 'lucide-react';

import { cn } from '~/lib/utils';

type MenuContentProps = MenuPrimitive.Popup.Props &
  Pick<
    MenuPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'anchor'
  >;

type MenuItemProps = MenuPrimitive.Item.Props;

const menuItemClassName = clsx(
  "group/dropdown-menu-item relative flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-[1rem] leading-6 outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-[#E2E3E5]/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
);

const MenuRoot = (props: MenuPrimitive.Root.Props) => {
  return <MenuPrimitive.Root {...props} />;
};

const MenuTrigger = (props: MenuPrimitive.Trigger.Props) => {
  return <MenuPrimitive.Trigger {...props} />;
};

const MenuSubmenuRoot = (props: MenuPrimitive.SubmenuRoot.Props) => {
  return <MenuPrimitive.SubmenuRoot {...props} />;
};

const MenuSubmenuTrigger = ({
  className,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props) => {
  return (
    <MenuPrimitive.SubmenuTrigger
      className={cn(menuItemClassName, className)}
      {...props}
    />
  );
};

const MenuContent = ({
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  anchor,
  className,
  ...props
}: MenuContentProps) => {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        anchor={anchor}
      >
        <MenuPrimitive.Popup
          className={cn(
            'z-50 max-h-(--available-height) w-(--anchor-width) min-w-64 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-xl bg-surface-neutral px-2 py-2 text-[#222] shadow-xs duration-100 outline-none data-closed:animate-out data-closed:overflow-hidden data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
};

const MenuItem = ({ className, ...props }: MenuItemProps) => {
  return (
    <MenuPrimitive.Item
      className={cn(menuItemClassName, className)}
      {...props}
    />
  );
};

const MenuSeparator = (props: MenuPrimitive.Separator.Props) => {
  return (
    <MenuPrimitive.Separator className="my-1.5 h-px bg-[#E2E3E5]" {...props} />
  );
};

function MenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  );
}

function MenuRadioItem({
  className,
  children,
  inset,
  ...props
}: MenuPrimitive.RadioItem.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      data-inset={inset}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-11 pl-4 text-[15px] leading-5.5 outline-hidden select-none focus:bg-[#E2E3E5]/40 data-disabled:pointer-events-none data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5.5 [&_svg:not([class*='stroke-'])]:stroke-[1.5px]",
        className,
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute right-4 flex items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator>
          <CheckIcon />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}

function MenuCheckboxItem({
  className,
  children,
  inset,
  ...props
}: MenuPrimitive.CheckboxItem.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      data-inset={inset}
      className={cn(
        "flex cursor-pointer items-center gap-2 py-2 pr-11 pl-4 text-[15px] leading-5.5 outline-hidden select-none focus:bg-[#F0F1F2] data-disabled:pointer-events-none data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5.5 [&_svg:not([class*='stroke-'])]:stroke-[1.5px]",
        className,
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute right-4 flex items-center justify-center"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator>
          <CheckIcon />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

export {
  MenuRoot,
  MenuTrigger,
  MenuSubmenuRoot,
  MenuSubmenuTrigger,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuRadioGroup,
  MenuRadioItem,
  MenuCheckboxItem,
};
